import React, { useState, useRef, useEffect } from 'react';
import { motion, useMotionValue, animate } from 'framer-motion';
import { ArrowsOutIcon, ArrowsOutCardinalIcon, TrashIcon, XIcon, LockIcon, LockOpenIcon } from '@phosphor-icons/react';
import { GenerationResult } from './Slides';
import { TextBubble } from './TextBubble';

interface CanvasItemProps {
    item: any;
    isSelected: boolean;
    onSelect: (shiftKey: boolean) => void;
    onUpdate?: (id: string | number, updates: any) => void;
    onDelete?: (id: string | number, url?: string) => void;
    onRemove?: (id: string | number) => void;
    onTogglePublic?: (id: string | number, currentStatus: boolean) => void;
    onToggleFavorite?: (id: string | number, currentStatus: boolean) => void;
    onSubmitToChallenge?: (id: string | number) => void;
    onInpaintComplete?: (id: string | number, newImageUrl: string) => void;
    onLoadError?: (id: string | number) => void;
    onViewFullscreen?: () => void;
    onAddToProject?: (img: string, prompt: string) => void;
    onSendToChat?: (img: string) => void;
    isInProject?: boolean;
    otherItems: Array<{ id: string | number, x: number; y: number; width: number; height: number }>;
    // Multi-select group drag
    selectedIds?: Set<string | number>;
    onGroupMove?: (deltaX: number, deltaY: number) => void;
    zoom: number;
    scaleRef?: React.MutableRefObject<number>;
    canvasTool: 'select' | 'pan' | 'text';
    lookbookBorder: 'thick' | 'thin' | 'none';
}

export const CanvasItem: React.FC<CanvasItemProps> = ({
    item,
    isSelected,
    onSelect,
    onUpdate,
    onDelete,
    onRemove,
    onTogglePublic,
    onToggleFavorite,
    onSubmitToChallenge,
    onInpaintComplete,
    onLoadError,
    onViewFullscreen,
    onAddToProject,
    onSendToChat,
    isInProject,
    otherItems,
    selectedIds,
    onGroupMove,
    zoom,
    scaleRef,
    canvasTool,
    lookbookBorder
}) => {
    // High-performance dragging using MotionValues
    // We bypass React state updates during drag to ensure 60fps+ tracking
    const motionX = useMotionValue(item.x);
    const motionY = useMotionValue(item.y);

    // We still track drag state for UI (z-index, etc)
    const [isDragging, setIsDragging] = useState(false);

    // Track inpaint mode to disable dragging
    const [isInpaintMode, setIsInpaintMode] = useState(false);

    // Refs for manual drag calculations
    const dragRef = useRef({
        startX: 0,
        startY: 0,
        initialX: 0,
        initialY: 0,
        lastX: 0,
        lastY: 0,
        vx: 0,
        vy: 0,
        lastTime: 0
    });

    // Keep zoom in a ref to ensure event handlers always have the latest value without re-binding
    const zoomRef = useRef(zoom);
    useEffect(() => { zoomRef.current = zoom; }, [zoom]);

    // Store natural image dimensions for double-click resize
    const naturalDimensionsRef = useRef<{ width: number; height: number } | null>(null);

    // Initial positioning if not set
    useEffect(() => {
        if (item.width === undefined && onUpdate) onUpdate(item.id, { width: 300, height: 300 * (1 / (item.aspectRatio || 1)) });
    }, []);

    // --- RESIZE LOGIC ---
    const [isResizing, setIsResizing] = useState(false);

    // Track initial mount to skip animation on first render
    const isInitialMount = useRef(true);

    // Sync MotionValues with props when not dragging (for Snaps, Undos, etc.)
    useEffect(() => {
        if (!isDragging) {
            // Skip animation on initial mount - just set position instantly
            if (isInitialMount.current) {
                isInitialMount.current = false;
                motionX.set(item.x);
                motionY.set(item.y);
                return;
            }
            // During Resize, we want INSTANT updates to prevent "Anchor Drift".
            // If we spring-animate position while resizing, the "fixed side" will wobble.
            if (isResizing) {
                motionX.set(item.x);
                motionY.set(item.y);
            } else {
                // Use imperative animation to smooth snap into place
                const springConfig = { type: "spring", stiffness: 400, damping: 40 } as const;
                animate(motionX, item.x, springConfig);
                animate(motionY, item.y, springConfig);
            }
        }
    }, [item.x, item.y, isDragging, isResizing, motionX, motionY]);

    const resizeRef = useRef<{ startX: number; startY: number; startWidth: number; startHeight: number; startLeft: number; startTop: number; direction: string } | null>(null);

    const handleResizeStart = (e: React.PointerEvent, direction: string) => {
        e.preventDefault();
        e.stopPropagation();
        setIsResizing(true);
        resizeRef.current = {
            startX: e.clientX,
            startY: e.clientY,
            startWidth: item.width || 300,
            startHeight: item.height || 300,
            startLeft: item.x,
            startTop: item.y,
            direction
        };

        window.addEventListener('pointermove', handleResizeMove);
        window.addEventListener('pointerup', handleResizeEnd);
    };

    // --- WHEEL (PAN) LOGIC ---
    const contentRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = contentRef.current;
        if (!el) return;

        const handleWheel = (e: WheelEvent) => {
            // Only capture wheel event if this item is selected (for panning)
            // Otherwise allow event to bubble to canvas for zooming
            if (!isSelected || item.type === 'text') return;

            e.stopPropagation();
            e.preventDefault();

            const sensitivity = 0.25;
            let currentX = item.pan_x ?? 50;
            let currentY = item.pan_y ?? 50;

            let dX = e.deltaX;
            let dY = e.deltaY;

            if (e.shiftKey && dX === 0 && dY !== 0) {
                dX = dY;
                dY = 0;
            }

            if (!e.shiftKey && Math.abs(dY) > Math.abs(dX) && item.settings?.aspect_ratio) {
                try {
                    const ratioParts = item.settings.aspect_ratio.split(':');
                    const ratioW = Number(ratioParts[0]);
                    const ratioH = Number(ratioParts[1]);

                    if (!isNaN(ratioW) && !isNaN(ratioH) && ratioH !== 0) {
                        const imageAspect = ratioW / ratioH;
                        const frameAspect = (Number(item.width) || 300) / (Number(item.height) || 300);

                        if (imageAspect > frameAspect) {
                            dX = dY;
                            dY = 0;
                        }
                    }
                } catch (err) { }
            }

            const newX = Math.min(100, Math.max(0, currentX + (dX * sensitivity)));
            const newY = Math.min(100, Math.max(0, currentY + (dY * sensitivity)));

            if (onUpdate) onUpdate(item.id, { pan_x: newX, pan_y: newY });
        };

        el.addEventListener('wheel', handleWheel, { passive: false });
        return () => el.removeEventListener('wheel', handleWheel);
    }, [isSelected, item, onUpdate]);

    const handleResizeMove = (e: PointerEvent) => {
        if (!resizeRef.current) return;
        const { startX, startY, startWidth, startHeight, startLeft, startTop, direction } = resizeRef.current;

        // Use Ref for latest scale to avoid stale props
        const scale = scaleRef?.current || zoomRef.current || 1;

        const deltaX = (e.clientX - startX) / scale;
        const deltaY = (e.clientY - startY) / scale;

        // Dynamic Border Merge based on current style
        const BORDER_MERGE = lookbookBorder === 'thick' ? 6 : (lookbookBorder === 'thin' ? 2 : 0);
        const PADDING = -BORDER_MERGE;
        const EPSILON = 0.5;

        // 1. Calculate Proposed Dimensions based on Mouse
        let dW = 0;
        let dH = 0;

        if (direction.includes('e')) dW = deltaX;
        if (direction.includes('w')) dW = -deltaX;
        if (direction.includes('s')) dH = deltaY;
        if (direction.includes('n')) dH = -deltaY;

        let proposedW = Math.max(50, startWidth + dW);
        let proposedH = Math.max(50, startHeight + dH);

        // Aspect Ratio Lock
        const aspect = startWidth / startHeight;

        // Only lock AR on corners, and if Shift is held
        if (e.shiftKey && direction.length > 1) {
            const ratioW = proposedW;
            const ratioH = proposedH;

            // Determine which axis determines the size (based on movement magnitude or just max)
            // Simple approach: Use W to drive H? Or whichever changed more relative to aspect?
            // Let's use the larger relative delta.
            if (Math.abs(dW) > Math.abs(dH * aspect)) {
                proposedH = proposedW / aspect;
            } else {
                proposedW = proposedH * aspect;
            }
        }

        let proposedX = Number(startLeft);
        let proposedY = Number(startTop);

        // Calculate proposed X/Y for anchor management
        if (direction.includes('w')) proposedX = startLeft + (startWidth - proposedW);
        if (direction.includes('n')) proposedY = startTop + (startHeight - proposedH);

        // 2. Identify Collisions
        // We distinguish between "Hard" blocks (already blocked in one axis) 
        // and "Corner" blocks (blocked only because of combined expansion).

        // Snap Candidates
        let snapRight = Infinity; let minDiffRight = Infinity;
        let snapLeft = -Infinity; let minDiffLeft = Infinity;
        let snapBottom = Infinity; let minDiffBottom = Infinity;
        let snapTop = -Infinity; let minDiffTop = Infinity;

        // Limit Candidates (Absolute Boundaries)
        let limitRight = Infinity;
        let limitLeft = -Infinity;
        let limitBottom = Infinity;
        let limitTop = -Infinity;

        // Robust Priority Logic:
        // If strictly horizontal handle (e, w), we prioritized Width.
        // If strictly vertical handle (n, s), we prioritize Height (i.e. !prioritizeWidth).
        // If diagonal (ne, se, sw, nw), we compare deltas.
        let prioritizeWidth = Math.abs(deltaX) > Math.abs(deltaY);

        if (direction === 'e' || direction === 'w') prioritizeWidth = true;
        if (direction === 'n' || direction === 's') prioritizeWidth = false;

        const SCREEN_SNAP_PX = 15; // Reduced from 30 to prevent "stickiness" feeling during subtle resizing
        const snapDist = SCREEN_SNAP_PX / scale;

        otherItems.forEach(other => {
            const ox = Number(other.x); const oy = Number(other.y);
            const ow = Number(other.width); const oh = Number(other.height);

            // ... (Existing Overlap/Limit Logic) ...
            // We duplicate Overlap checks here for clarity or reuse variables if possible
            // Re-calculating as we are inside the same loop structure in replacement (Wait, I can't easily reuse vars across replacement)
            // I will inject the snap logic alongside limit logic inside the loop.

            // CHECK ALIGNMENT using minimal tolerance (EPSILON)
            // We want to detect ANY overlap in the orthogonal axis to apply limits
            const strictYOverlap = (startTop < oy + oh - EPSILON) && (startTop + startHeight > oy + EPSILON);
            const strictXOverlap = (startLeft < ox + ow - EPSILON) && (startLeft + startWidth > ox + EPSILON);

            const vOverlapProposed = (proposedY < oy + oh - EPSILON) && (proposedY + proposedH > oy + EPSILON);
            const hOverlapProposed = (proposedX < ox + ow - EPSILON) && (proposedX + proposedW > ox + EPSILON);

            // Limit Logic (Preserve Existing)
            let isHardHorizontal = false;
            let isHardVertical = false;

            if (vOverlapProposed && strictYOverlap) isHardHorizontal = true;
            if (hOverlapProposed && strictXOverlap) isHardVertical = true;

            // Horizontal Limits & Snap
            if (vOverlapProposed) {
                // Relaxed neighbor direction check:
                // Is neighbor strictly to the West/East?
                // Use EPSILON for strictly-out detection
                // Allow "neighbors" that are already slightly overlapping (merged) to count as neighbors
                const isWest = ox + ow <= startLeft + BORDER_MERGE + EPSILON;
                const isEast = ox >= startLeft + startWidth - BORDER_MERGE - EPSILON;

                if (direction.includes('e') && isEast) {
                    if (isHardHorizontal || !prioritizeWidth) limitRight = Math.min(limitRight, ox - PADDING);
                    // Snap Logic: Even if not hard limit, we snap
                    const diff = Math.abs((ox - PADDING) - (proposedX + proposedW));
                    if (diff < snapDist && diff < minDiffRight) { minDiffRight = diff; snapRight = ox - PADDING; }
                }
                if (direction.includes('w') && isWest) {
                    if (isHardHorizontal || !prioritizeWidth) limitLeft = Math.max(limitLeft, ox + ow + PADDING);
                    const diff = Math.abs((ox + ow + PADDING) - proposedX);
                    if (diff < snapDist && diff < minDiffLeft) { minDiffLeft = diff; snapLeft = ox + ow + PADDING; }
                }
            }

            // Vertical Limits & Snap
            if (hOverlapProposed) {
                const isNorth = oy + oh <= startTop + BORDER_MERGE + EPSILON;
                const isSouth = oy >= startTop + startHeight - BORDER_MERGE - EPSILON;

                if (direction.includes('s') && isSouth) {
                    if (isHardVertical || prioritizeWidth) {
                        limitBottom = Math.min(limitBottom, oy - PADDING);
                    }
                    const diff = Math.abs((oy - PADDING) - (proposedY + proposedH));
                    if (diff < snapDist && diff < minDiffBottom) { minDiffBottom = diff; snapBottom = oy - PADDING; }
                }
                if (direction.includes('n') && isNorth) {
                    if (isHardVertical || prioritizeWidth) {
                        limitTop = Math.max(limitTop, oy + oh + PADDING);
                    }
                    const diff = Math.abs((oy + oh + PADDING) - proposedY);
                    if (diff < snapDist && diff < minDiffTop) { minDiffTop = diff; snapTop = oy + oh + PADDING; }
                }
            }
        });

        // Apply Snaps (Priority over raw mouse, but Limits override Snaps if blocked)
        if (direction.includes('e') && snapRight !== Infinity) proposedW = Math.max(50, snapRight - proposedX);
        if (direction.includes('w') && snapLeft !== -Infinity) {
            const oldRight = proposedX + proposedW;
            proposedX = snapLeft; proposedW = Math.max(50, oldRight - proposedX);
        }
        if (direction.includes('s') && snapBottom !== Infinity) proposedH = Math.max(50, snapBottom - proposedY);
        if (direction.includes('n') && snapTop !== -Infinity) {
            const oldBottom = proposedY + proposedH;
            proposedY = snapTop; proposedH = Math.max(50, oldBottom - proposedY);
        }




        // 3. Apply Calculated Limits with Aspect Ratio Re-Verification
        let newWidth = proposedW;
        let newHeight = proposedH;
        let newX = proposedX;
        let newY = proposedY;

        // Apply Limits & Recalculate other dimension if Aspect Locked
        const isAspectLocked = e.shiftKey && direction.length > 1;

        if (direction.includes('e') && newX + newWidth > limitRight) {
            newWidth = Math.max(50, limitRight - newX);
            if (isAspectLocked) {
                newHeight = newWidth / aspect;
                // If this Height change requires Y adjustment (e.g. 'ne'), handle it:
                if (direction.includes('n')) newY = (startTop + startHeight) - newHeight;
                // Double Check Height Limit?
                // If we squashed Height down, we might hit limitTop/Bottom?
                // For simplicity, we assume primary Limit hit dictates the size.
            }
        }
        if (direction.includes('w') && newX < limitLeft) {
            const maxW = (startLeft + startWidth) - limitLeft;
            newWidth = Math.max(50, maxW);
            newX = (startLeft + startWidth) - newWidth;
            if (isAspectLocked) {
                newHeight = newWidth / aspect;
                if (direction.includes('n')) newY = (startTop + startHeight) - newHeight;
            }
        }

        if (direction.includes('s') && newY + newHeight > limitBottom) {
            newHeight = Math.max(50, limitBottom - newY);
            if (isAspectLocked) {
                newWidth = newHeight * aspect;
                if (direction.includes('w')) newX = (startLeft + startWidth) - newWidth;
            }
        }
        if (direction.includes('n') && newY < limitTop) {
            const maxH = (startTop + startHeight) - limitTop;
            newHeight = Math.max(50, maxH);
            newY = (startTop + startHeight) - newHeight;
            if (isAspectLocked) {
                newWidth = newHeight * aspect;
                if (direction.includes('w')) newX = (startLeft + startWidth) - newWidth;
            }
        }

        if (onUpdate) onUpdate(item.id, { x: newX, y: newY, width: newWidth, height: newHeight });
    };

    const handleResizeEnd = () => {
        setIsResizing(false);
        resizeRef.current = null;
        window.removeEventListener('pointermove', handleResizeMove);
        window.removeEventListener('pointerup', handleResizeEnd);
    };

    // --- MANUAL DRAG LOGIC (Optimized) ---
    // Solves the "cursor goes too far" issue when zoomed out
    const handlePointerDown = (e: React.PointerEvent) => {
        // Prevent interaction on middle click (panning)
        if (e.button === 1) return;

        // Disable dragging when in inpaint mode (user is drawing on mask)
        if (isInpaintMode) return;

        if (canvasTool !== 'select' || isResizing || item.is_locked) return;
        e.preventDefault();
        e.stopPropagation();
        onSelect(e.shiftKey);

        setIsDragging(true);
        dragRef.current = {
            startX: e.clientX,
            startY: e.clientY,
            initialX: motionX.get(),
            initialY: motionY.get(),
            lastX: e.clientX,
            lastY: e.clientY,
            vx: 0,
            vy: 0,
            lastTime: performance.now()
        };

        window.addEventListener('pointermove', handlePointerMove);
        window.addEventListener('pointerup', handlePointerUp);
    };

    const handlePointerMove = (e: PointerEvent) => {
        // We removed the (!isDragging) check because this function is added/removed explicitly
        // and 'isDragging' state might be stale in this closure.

        // Use Ref for latest scale to avoid stale props
        const scale = scaleRef?.current || zoomRef.current || 1;
        const dx = (e.clientX - dragRef.current.startX) / scale;
        const dy = (e.clientY - dragRef.current.startY) / scale;

        // Calculate velocity for momentum
        const now = performance.now();
        const dt = now - dragRef.current.lastTime;
        if (dt > 0) {
            dragRef.current.vx = (e.clientX - dragRef.current.lastX) / dt; // screen pixels per ms
            dragRef.current.vy = (e.clientY - dragRef.current.lastY) / dt;
            dragRef.current.lastX = e.clientX;
            dragRef.current.lastY = e.clientY;
            dragRef.current.lastTime = now;
        }

        // Direct updates to MotionValue (Bypasses React Render -> High Performance)
        motionX.set(dragRef.current.initialX + dx);
        motionY.set(dragRef.current.initialY + dy);
    };

    const handlePointerUp = (e: PointerEvent) => {
        // Keep isDragging true until after position is finalized
        window.removeEventListener('pointermove', handlePointerMove);
        window.removeEventListener('pointerup', handlePointerUp);

        if (!dragRef.current) return;

        const currentX = dragRef.current.initialX;
        const currentY = dragRef.current.initialY;
        const myW = Number(item.width || 300);
        const myH = Number(item.height || 300);
        const scale = scaleRef?.current || zoomRef.current || 1;

        // Final raw position from drag
        let rawX = currentX + ((e.clientX - dragRef.current.startX) / scale);
        let rawY = currentY + ((e.clientY - dragRef.current.startY) / scale);

        // Momentum disabled to prevent "Throwing" issues when zoomed out
        // if (Math.abs(dragRef.current.vx) > 0.5) rawX += (dragRef.current.vx * 200) / scale;
        // if (Math.abs(dragRef.current.vy) > 0.5) rawY += (dragRef.current.vy * 200) / scale;

        // --- SMART SNAP & COLLISION ---
        if (item.type === 'text') {
            // Text bubbles float freely and don't collide/snap
            animate(motionX, rawX, { type: 'spring', stiffness: 400, damping: 40 });
            animate(motionY, rawY, { type: 'spring', stiffness: 400, damping: 40 });
            if (onUpdate) onUpdate(item.id, { x: rawX, y: rawY });
            // Group move: move other selected items by the same delta
            if (selectedIds && selectedIds.size > 1 && selectedIds.has(item.id) && onGroupMove) {
                const deltaX = rawX - currentX;
                const deltaY = rawY - currentY;
                onGroupMove(deltaX, deltaY);
            }
            setIsDragging(false);
            return;
        }

        // Dynamic Snap Distance: Ensure it feels consistent (e.g. ~30px on screen) regardless of zoom


        const BORDER_MERGE = lookbookBorder === 'thick' ? 6 : (lookbookBorder === 'thin' ? 2 : 0);
        const PADDING = -BORDER_MERGE; // Match Hero.tsx gutter to merge borders
        const COLLISION_MARGIN = 0;
        const EPSILON = 0.05; // Forgive micro-overlaps

        // Helper: Check collision (Permissive with Border Merge)
        const isColliding = (cx: number, cy: number, cw?: number, ch?: number) => {
            const currentW = cw ?? myW;
            const currentH = ch ?? myH;

            for (const other of otherItems) {
                const ox = Number(other.x);
                const oy = Number(other.y);
                const ow = Number(other.width);
                const oh = Number(other.height);

                // Calculate overlap depth in both dimensions
                const xOverlap = Math.min(cx + currentW, ox + ow) - Math.max(cx, ox);
                const yOverlap = Math.min(cy + currentH, oy + oh) - Math.max(cy, oy);

                // A collision exists only if we overlap MORE than the allowed merge amount in BOTH axes
                // We add EPSILON to allow exactly BORDER_MERGE (6.0) to be valid
                if (xOverlap > BORDER_MERGE + EPSILON && yOverlap > BORDER_MERGE + EPSILON) {
                    return true;
                }
            }
            return false;
        };

        // EARLY EXIT: If dropped in empty space (no strict overlap), just stay there
        // Use strict check for "Am I touching?" but permissive for "Am I valid?"
        const hasAnyOverlap = otherItems.some(other => {
            const ox = Number(other.x);
            const oy = Number(other.y);
            const ow = Number(other.width);
            const oh = Number(other.height);
            // Strict check for initial overlap detection
            return rawX < ox + ow && rawX + myW > ox && rawY < oy + oh && rawY + myH > oy;
        });

        // 1. Generate Candidates
        type Candidate = { val: number; type: 'snap' | 'raw' };

        // Dynamic Snap Distance logic
        const DRAG_SNAP_PX = 30;
        let activeSnapDist = DRAG_SNAP_PX / scale;

        // CRITICAL: If overlapping, find ANY escape (Active Snap becomes Infinity)
        if (hasAnyOverlap) {
            activeSnapDist = Infinity;
        }

        const xCandidates: Candidate[] = [{ val: rawX, type: 'raw' }];
        const yCandidates: Candidate[] = [{ val: rawY, type: 'raw' }];

        otherItems.forEach(other => {
            const ox = Number(other.x); const oy = Number(other.y); const ow = Number(other.width); const oh = Number(other.height);

            // Edges (Padded)
            // Snap My Left to Other Right
            if (activeSnapDist === Infinity || Math.abs(rawX - (ox + ow + PADDING)) < activeSnapDist) xCandidates.push({ val: ox + ow + PADDING, type: 'snap' });
            // Snap My Right to Other Left
            if (activeSnapDist === Infinity || Math.abs(rawX - (ox - myW - PADDING)) < activeSnapDist) xCandidates.push({ val: ox - myW - PADDING, type: 'snap' });
            // Align Lefts (No Padding)
            if (activeSnapDist === Infinity || Math.abs(rawX - ox) < activeSnapDist) xCandidates.push({ val: ox, type: 'snap' });

            // Snap My Top to Other Bottom
            if (activeSnapDist === Infinity || Math.abs(rawY - (oy + oh + PADDING)) < activeSnapDist) yCandidates.push({ val: oy + oh + PADDING, type: 'snap' });
            // Snap My Bottom to Other Top
            if (activeSnapDist === Infinity || Math.abs(rawY - (oy - myH - PADDING)) < activeSnapDist) yCandidates.push({ val: oy - myH - PADDING, type: 'snap' });
            // Align Tops (No Padding)
            if (activeSnapDist === Infinity || Math.abs(rawY - oy) < activeSnapDist) yCandidates.push({ val: oy, type: 'snap' });
        });

        // 2. Score Positions
        type PosCandidate = { x: number; y: number; score: number };
        const candidates: PosCandidate[] = [];
        xCandidates.forEach(xc => {
            yCandidates.forEach(yc => {
                const dist = Math.sqrt(Math.pow(xc.val - rawX, 2) + Math.pow(yc.val - rawY, 2));
                let score = dist;
                if (xc.type === 'snap') score -= 1000;
                if (yc.type === 'snap') score -= 1000;
                candidates.push({ x: xc.val, y: yc.val, score });
            });
        });

        candidates.sort((a, b) => a.score - b.score);

        // 3. Select Best Valid
        let finalX = currentX;
        let finalY = currentY;
        let found = false;

        for (const cand of candidates) {
            if (!isColliding(cand.x, cand.y)) {
                finalX = cand.x; finalY = cand.y; found = true; break;
            }
        }

        // 4. Fallback: Spiral Search (If edges failed, find ANY spot)
        if (!found) {
            // AUTO-FIT LOGIC START (GLOBAL VOID SEARCH)
            // Strategy: Find ALL empty rectangular regions (voids) defined by the grid of existing items.
            // Snap to the void that has the highest overlap area with the dragged item.

            // 1. Collect Unique Grid Lines
            const xLines = new Set<number>();
            const yLines = new Set<number>();
            xLines.add(-Infinity); xLines.add(Infinity);
            yLines.add(-Infinity); yLines.add(Infinity);

            // Add item edges as grid lines
            otherItems.forEach(o => {
                const ox = Number(o.x), oy = Number(o.y), ow = Number(o.width), oh = Number(o.height);
                xLines.add(ox - PADDING); xLines.add(ox + ow + PADDING);
                yLines.add(oy - PADDING); yLines.add(oy + oh + PADDING);
            });

            // Allow bounds of the dragged item itself to define the Search Area? 
            // No, strictly snap to *existing* structure.

            const sortedX = Array.from(xLines).sort((a, b) => a - b);
            const sortedY = Array.from(yLines).sort((a, b) => a - b);

            let bestVoid = null;
            let maxOverlapArea = 0;
            const MIN_SIZE = 50;

            // 2. Iterate through all potential cells defined by the grid
            // We only check cells that are within the bounding box of our Dragged Item (+ margin) 
            // to optimize performance (though N is small so brute force is fine)

            const itemL = rawX; const itemR = rawX + myW;
            const itemT = rawY; const itemB = rawY + myH;

            for (let i = 0; i < sortedX.length - 1; i++) {
                const x1 = sortedX[i];
                const x2 = sortedX[i + 1];
                if (x1 === -Infinity || x2 === Infinity) continue; // Skip infinite bounds
                const cellW = x2 - x1;
                if (cellW < MIN_SIZE) continue;

                // Optimization: Skip columns that don't overlap our item X-wise
                if (x2 < itemL || x1 > itemR) continue;

                for (let j = 0; j < sortedY.length - 1; j++) {
                    const y1 = sortedY[j];
                    const y2 = sortedY[j + 1];
                    if (y1 === -Infinity || y2 === Infinity) continue;
                    const cellH = y2 - y1;
                    if (cellH < MIN_SIZE) continue;

                    // Optimization: Skip rows that don't overlap our item Y-wise
                    if (y2 < itemT || y1 > itemB) continue;

                    // 3. Collision Check: Is this cell actually empty?
                    // We shrink the check slightly to ignore grazing touches
                    if (isColliding(x1 + 2, y1 + 2, cellW - 4, cellH - 4)) continue;

                    // 4. Calculate Overlap
                    const overlapW = Math.min(itemR, x2) - Math.max(itemL, x1);
                    const overlapH = Math.min(itemB, y2) - Math.max(itemT, y1);

                    if (overlapW > 0 && overlapH > 0) {
                        const area = overlapW * overlapH;
                        if (area > maxOverlapArea) {
                            maxOverlapArea = area;
                            bestVoid = { x: x1, y: y1, w: cellW, h: cellH };
                        }
                    }
                }
            }

            // 5. Apply Best Fit
            if (bestVoid) {
                finalX = bestVoid.x; finalY = bestVoid.y;
                const fitW = bestVoid.w; const fitH = bestVoid.h;
                found = true;
                if (onUpdate) onUpdate(item.id, { x: finalX, y: finalY, width: fitW, height: fitH });
                animate(motionX, finalX, { type: 'spring', stiffness: 400, damping: 40 });
                animate(motionY, finalY, { type: 'spring', stiffness: 400, damping: 40 });
                // Group move: move other selected items by the same delta
                if (selectedIds && selectedIds.size > 1 && selectedIds.has(item.id) && onGroupMove) {
                    const deltaX = finalX - currentX;
                    const deltaY = finalY - currentY;
                    onGroupMove(deltaX, deltaY);
                }
                setIsDragging(false);
                return;
            }
            // AUTO-FIT LOGIC END

            let radius = 10;
            let angle = 0;
            const maxRadius = 1000; // Don't freeze browser

            while (radius < maxRadius) {
                // Spiral Formula: x = r * cos(a), y = r * sin(a)
                // Check 8 points per radius ring (or more)
                // Simplify to checking increments

                // Try 8 points around circle
                for (let i = 0; i < 8; i++) {
                    const checkX = rawX + (radius * Math.cos(i * Math.PI / 4));
                    const checkY = rawY + (radius * Math.sin(i * Math.PI / 4));
                    if (!isColliding(checkX, checkY)) {
                        finalX = checkX; finalY = checkY; found = true; break;
                    }
                }
                if (found) break;
                radius += 50; // Step out
            }
        }
        // If still not found, we sadly return to origin (finalX was init to currentX)



        // Update local motion value to the final snapped position immediately
        // This prevents the item from "jumping back" to the old prop position while waiting for React state update
        let groupDeltaX = 0;
        let groupDeltaY = 0;

        if (found) {
            animate(motionX, finalX, { type: 'spring', stiffness: 400, damping: 40 });
            animate(motionY, finalY, { type: 'spring', stiffness: 400, damping: 40 });
            if (onUpdate) onUpdate(item.id, { x: finalX, y: finalY });
            groupDeltaX = finalX - currentX;
            groupDeltaY = finalY - currentY;
        } else {
            // No valid position found - snap to nearest side of closest image
            let bestX = rawX;
            let bestY = rawY;
            let bestDist = Infinity;

            for (const other of otherItems) {
                const ox = Number(other.x);
                const oy = Number(other.y);
                const ow = Number(other.width);
                const oh = Number(other.height);

                // Try each side of this image
                const sides = [
                    { x: ox + ow + PADDING, y: oy }, // Right side
                    { x: ox - myW - PADDING, y: oy }, // Left side
                    { x: ox, y: oy + oh + PADDING }, // Bottom
                    { x: ox, y: oy - myH - PADDING }, // Top
                ];

                for (const side of sides) {
                    const dist = Math.sqrt(Math.pow(side.x - rawX, 2) + Math.pow(side.y - rawY, 2));
                    if (dist < bestDist && !isColliding(side.x, side.y)) {
                        bestDist = dist;
                        bestX = side.x;
                        bestY = side.y;
                    }
                }
            }

            animate(motionX, bestX, { type: 'spring', stiffness: 400, damping: 40 });
            animate(motionY, bestY, { type: 'spring', stiffness: 400, damping: 40 });
            if (onUpdate) onUpdate(item.id, { x: bestX, y: bestY });
            groupDeltaX = bestX - currentX;
            groupDeltaY = bestY - currentY;
        }

        // Group move: move other selected items by the same delta
        if (selectedIds && selectedIds.size > 1 && selectedIds.has(item.id) && onGroupMove) {
            onGroupMove(groupDeltaX, groupDeltaY);
        }

        // Set dragging false AFTER position update to prevent sync effect from reverting
        setIsDragging(false);
    };

    // --- DOUBLE-CLICK RESIZE TO TRUE SIZE ---
    const handleDoubleClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        // Only works for image items with known natural dimensions
        if (item.type === 'text' || !naturalDimensionsRef.current) return;

        const { width: natW, height: natH } = naturalDimensionsRef.current;
        const currentX = item.x;
        const currentY = item.y;

        // Calculate collision detection helpers
        const BORDER_MERGE = lookbookBorder === 'thick' ? 6 : (lookbookBorder === 'thin' ? 2 : 0);
        const PADDING = -BORDER_MERGE;
        const EPSILON = 0.05;

        // Helper: Check collision at position with given dimensions
        const isCollidingAt = (cx: number, cy: number, cw: number, ch: number) => {
            for (const other of otherItems) {
                const ox = Number(other.x);
                const oy = Number(other.y);
                const ow = Number(other.width);
                const oh = Number(other.height);

                const xOverlap = Math.min(cx + cw, ox + ow) - Math.max(cx, ox);
                const yOverlap = Math.min(cy + ch, oy + oh) - Math.max(cy, oy);

                if (xOverlap > BORDER_MERGE + EPSILON && yOverlap > BORDER_MERGE + EPSILON) {
                    return true;
                }
            }
            return false;
        };

        // Try to place at current position first
        if (!isCollidingAt(currentX, currentY, natW, natH)) {
            // No collision - resize in place
            if (onUpdate) onUpdate(item.id, { width: natW, height: natH, pan_x: 50, pan_y: 50 });
            return;
        }

        // Collision detected - find closest available position
        // Strategy: Try snapping to edges of other items, preferring positions closest to original

        type PosCandidate = { x: number; y: number; dist: number };
        const candidates: PosCandidate[] = [];

        // Generate candidate positions based on other items' edges
        otherItems.forEach(other => {
            const ox = Number(other.x);
            const oy = Number(other.y);
            const ow = Number(other.width);
            const oh = Number(other.height);

            // Right of this item
            candidates.push({ x: ox + ow + PADDING, y: oy, dist: 0 });
            candidates.push({ x: ox + ow + PADDING, y: currentY, dist: 0 });

            // Left of this item
            candidates.push({ x: ox - natW - PADDING, y: oy, dist: 0 });
            candidates.push({ x: ox - natW - PADDING, y: currentY, dist: 0 });

            // Below this item
            candidates.push({ x: ox, y: oy + oh + PADDING, dist: 0 });
            candidates.push({ x: currentX, y: oy + oh + PADDING, dist: 0 });

            // Above this item
            candidates.push({ x: ox, y: oy - natH - PADDING, dist: 0 });
            candidates.push({ x: currentX, y: oy - natH - PADDING, dist: 0 });
        });

        // Calculate distance from original position for each candidate
        candidates.forEach(c => {
            c.dist = Math.sqrt(Math.pow(c.x - currentX, 2) + Math.pow(c.y - currentY, 2));
        });

        // Sort by distance (closest first)
        candidates.sort((a, b) => a.dist - b.dist);

        // Find first non-colliding position
        let finalX = currentX;
        let finalY = currentY;
        let found = false;

        for (const cand of candidates) {
            if (!isCollidingAt(cand.x, cand.y, natW, natH)) {
                finalX = cand.x;
                finalY = cand.y;
                found = true;
                break;
            }
        }

        // Fallback: Spiral search outward from current position
        if (!found) {
            let radius = 50;
            const maxRadius = 2000;

            while (radius < maxRadius && !found) {
                for (let i = 0; i < 16; i++) {
                    const angle = (i / 16) * 2 * Math.PI;
                    const checkX = currentX + radius * Math.cos(angle);
                    const checkY = currentY + radius * Math.sin(angle);

                    if (!isCollidingAt(checkX, checkY, natW, natH)) {
                        finalX = checkX;
                        finalY = checkY;
                        found = true;
                        break;
                    }
                }
                radius += 50;
            }
        }

        // Apply the resize and reposition with animation
        animate(motionX, finalX, { type: 'spring', stiffness: 400, damping: 40 });
        animate(motionY, finalY, { type: 'spring', stiffness: 400, damping: 40 });
        if (onUpdate) onUpdate(item.id, { x: finalX, y: finalY, width: natW, height: natH, pan_x: 50, pan_y: 50 });
    };

    return (
        <motion.div
            id={String(item.id)}
            onPointerDown={onUpdate ? handlePointerDown : undefined}
            onDoubleClick={onUpdate ? handleDoubleClick : undefined}
            // Use MotionValues for x/y to enable high-performance drag
            style={{
                x: motionX,
                y: motionY,
                zIndex: isDragging || isSelected ? 100 : (item.type === 'text' ? 50 : 1),
                touchAction: 'none',
                position: 'absolute'
            }}
            // Only animate dimensions declaratively
            animate={{
                width: item.width,
                height: item.height
            }}
            transition={{ duration: 0 }}
            className={`group absolute ${isSelected ? 'z-50' : 'z-10'}`}
        >
            {/* Selection Ring & Magnetic Guides */}
            <div
                className={`absolute -inset-1 border-2 rounded-xl transition-opacity pointer-events-none ${isSelected ? 'border-indigo-500 opacity-100' : 'border-transparent opacity-0 group-hover:opacity-50'}`}
            />

            {/* Content */}
            <div
                ref={contentRef}
                className={`w-full h-full ${isSelected ? 'stop-wheel' : ''} ${item.type === 'text'
                    ? 'select-text cursor-text' // Text bubbles need selection for typing
                    : `select-none bg-white ${lookbookBorder === 'thick' ? 'p-1.5' : (lookbookBorder === 'thin' ? 'p-[2px]' : 'p-0')}`
                    }`}
                // onWheel handled by useEffect for passive: false support
                onPointerDown={(e) => {
                    // For text items, stop propagation so we can select text without dragging immediately
                    // But we still need to allow selection of the item itself if checking target
                    if (item.type === 'text') {
                        e.stopPropagation();
                    }
                }}
            >
                {item.type === 'text' ? (
                    <TextBubble
                        id={item.id}
                        text={item.text_content || 'Double click to edit'}
                        width={item.width}
                        height={item.height}
                        style={item.bubble_style || 'speech'}
                        fontFamily={item.font_family}
                        color={item.text_color}
                        bubbleColor={item.bubble_color}
                        align={item.text_align}
                        onUpdate={onUpdate}
                        isSelected={isSelected}
                    />
                ) : (
                    <GenerationResult
                        id={item.id}
                        img={item.img || item.src}
                        prompt={item.prompt}
                        model={item.model}
                        isLoading={item.isLoading}
                        isPublic={item.is_public ?? false}
                        isFavorited={item.is_favorited ?? false}
                        isLocked={item.is_locked ?? false}
                        panX={item.pan_x}
                        panY={item.pan_y}
                        zoom={zoom}
                        onDelete={onDelete}
                        onRemove={onRemove}
                        onTogglePublic={onTogglePublic}
                        onToggleFavorite={onToggleFavorite}
                        onSubmitToChallenge={onSubmitToChallenge}
                        onInpaintComplete={onInpaintComplete}
                        onInpaintModeChange={(mode) => setIsInpaintMode(mode)}
                        onLoadError={onLoadError}
                        onViewFullscreen={onViewFullscreen}
                        onAddToProject={onAddToProject}
                        onSendToChat={onSendToChat}
                        isInProject={isInProject}
                        onToggleLock={(id, currentStatus) => {
                            if (onUpdate) onUpdate(id, { is_locked: !currentStatus });
                        }}
                        onImageLoad={(w, h) => {
                            // Store natural dimensions for double-click resize
                            naturalDimensionsRef.current = { width: w, height: h };

                            const ratio = w / h;

                            // Only auto-resize items with default 300x300 dimensions (newly added items)
                            // Items with saved dimensions should keep them (don't resize on every load)
                            if (item.width === 300 && item.height === 300) {
                                if (item.type === 'Recalled') {
                                    // For recalled images from gallery, use true natural size
                                    if (onUpdate) onUpdate(item.id, {
                                        width: w,
                                        height: h,
                                        aspectRatio: ratio
                                    });
                                } else {
                                    // For generated images, use 300px width with correct aspect ratio
                                    if (onUpdate) onUpdate(item.id, {
                                        width: 300,
                                        height: 300 / ratio,
                                        aspectRatio: ratio
                                    });
                                }
                            }
                        }}
                    />
                )}
            </div>

            {/* Resize Handles - Only visible when selected and editable */}
            {isSelected && onUpdate && (
                <>
                    <div className="absolute -top-1.5 -left-1.5 w-4 h-4 bg-white border border-zinc-300 rounded-full cursor-nwse-resize z-50 shadow-sm" onPointerDown={(e) => handleResizeStart(e, 'nw')} />
                    <div className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-white border border-zinc-300 rounded-full cursor-nesw-resize z-50 shadow-sm" onPointerDown={(e) => handleResizeStart(e, 'ne')} />
                    <div className="absolute -bottom-1.5 -left-1.5 w-4 h-4 bg-white border border-zinc-300 rounded-full cursor-nesw-resize z-50 shadow-sm" onPointerDown={(e) => handleResizeStart(e, 'sw')} />
                    <div className="absolute -bottom-1.5 -right-1.5 w-4 h-4 bg-white border border-zinc-300 rounded-full cursor-nwse-resize z-50 shadow-sm" onPointerDown={(e) => handleResizeStart(e, 'se')} />
                </>
            )}

        </motion.div>
    );
};
